// Standard library imports
import React, { useState, useEffect } from 'react';
// External library imports
import { toast } from 'react-toastify';
import moment from 'moment';
import _ from 'lodash';
import { UilImport } from '@iconscout/react-unicons';

// Internal module imports
import AfrrClasses from './index.module.css';
import { DropdownComponent } from '../../../../components/Inputs/Input';
import classes from '../../../../styles/AllDevices.module.css';
import CustomTimePicker from '../../../../components/Inputs/TimePicker/TimePicker';
import { useLoader, useQueryParamWithDefault } from '../../../../hooks';
import Typography from '../../../../components/Typography/Typography';
import Table from '../../../../components/Table/Table';
import DatePicker from '../../../../components/Inputs/DatePicker/DatePicker';
import { RootAfrrService } from '../../../../services/RootAfrrService';
import { isValidTimeRange, momentTimeFormater } from '../../../../utils/timeHelper';
import CustomTooltip from '../../../../components/CustomToolTip/CustomTooltip';
import { DownloadAsExcel } from '../../../../utils/downloadAsExcel';
import { DateParam, StringParam } from 'use-query-params';

const LIMIT = 15;

const EicDataLogs = ({ eicCodeOptions, cId }) => {
    const curCetDateTime = moment().tz('Europe/Berlin');
    const [startLoader, stopLoader] = useLoader();
    const [date, setDate] = useQueryParamWithDefault('date', DateParam, new Date(curCetDateTime.format()), cId, { clearOnUnmount: false });
    const [startTime, setStartTime] = useQueryParamWithDefault('startTime', StringParam, momentTimeFormater().subtract(15, 'minutes').format('HH:mm:ss'), cId, {
        clearOnUnmount: false,
    });
    const [endTime, setEndTime] = useQueryParamWithDefault('endTime', StringParam, momentTimeFormater().format('HH:mm:ss'), cId, { clearOnUnmount: false });
    const [selectedEicCode, setSelectedEicCode] = useQueryParamWithDefault('eic', StringParam, eicCodeOptions[0]?.value, cId, { clearOnUnmount: false });
    const [tableData, setTableData] = useState([]);
    const [skip, setSkip] = useState(0);
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        if (eicCodeOptions?.length) setSelectedEicCode(selectedEicCode ? selectedEicCode : eicCodeOptions[0]?.value);
    }, [eicCodeOptions]);

    useEffect(() => {
        if (selectedEicCode && date && startTime && endTime) getEicLogs(true);
    }, [selectedEicCode, skip, startTime]);

    const getEicLogs = (paginated = true) => {
        if (!isValidTimeRange(startTime, endTime)) {
            toast.error('Invalid time range!');
            return;
        }
        const params = {
            eicCode: selectedEicCode,
            date: moment(date).format('YYYY-MM-DD'),
            startTime: startTime,
            endTime: endTime,
            skip: skip,
            limit: LIMIT,
            isPaginatedData: paginated,
        };
        const handler = paginated ? handleLogsSuccess : handleDownload;
        RootAfrrService.GetEicLogs(params, startLoader, handler, handleError, stopLoader);
    };

    const handleLogsSuccess = ({ data }) => {
        const processedData = data.data.logs.map((item) => ({
            timestamp: momentTimeFormater(item.timestamp).format('YYYY-MM-DD HH:mm:ss'),
            dk1H: _.get(item, ['picassoHeartbeatSignalDk1', 'value'], '--'),
            dk1SR: _.get(item, ['picassoSetpointRequestedActivationDk1', 'value'], '--'),
            dk1SRF: _.get(item, ['picassoSetpointRequestedActivationFeedbackDk1', 'value'], '--'),
            dk1AAP: _.get(item, ['picassoAbsoluteActivationSignalDk1', 'value'], '--'),
            dk1CAP: _.get(item, ['picassoCurrentActivationSignalDk1', 'value'], '--'),
            dk2H: _.get(item, ['picassoHeartbeatSignalDk2', 'value'], '--'),
            dk2SR: _.get(item, ['picassoSetpointRequestedActivationDk2', 'value'], '--'),
            dk2SRF: _.get(item, ['picassoSetpointRequestedActivationFeedbackDk2', 'value'], '--'),
            dk2AAP: _.get(item, ['picassoAbsoluteActivationSignalDk2', 'value'], '--'),
            dk2CAP: _.get(item, ['picassoCurrentActivationSignalDk2', 'value'], '--'),
        }));
        setTableData(processedData);
        setTotalCount(data.data.count);
    };

    const handlePageChange = (pageno) => {
        setSkip(LIMIT * pageno);
        setPage(pageno);
    };

    const handleError = (err) => {
        console.log(err);
        if (err && err.response) toast.error(err.response.data.message);
    };

    const handleDownload = ({ data }) => {
        const processedData = data.data.logs.map((item) => ({
            timestamp: momentTimeFormater(item.timestamp).format('YYYY-MM-DD HH:mm:ss'),
            dk1H: _.get(item, ['picassoHeartbeatSignalDk1', 'value'], '--'),
            dk1SR: _.get(item, ['picassoSetpointRequestedActivationDk1', 'value'], '--'),
            dk1SRF: _.get(item, ['picassoSetpointRequestedActivationFeedbackDk1', 'value'], '--'),
            dk1AAP: _.get(item, ['picassoAbsoluteActivationSignalDk1', 'value'], '--'),
            dk1CAP: _.get(item, ['picassoCurrentActivationSignalDk1', 'value'], '--'),
            dk2H: _.get(item, ['picassoHeartbeatSignalDk2', 'value'], '--'),
            dk2SR: _.get(item, ['picassoSetpointRequestedActivationDk2', 'value'], '--'),
            dk2SRF: _.get(item, ['picassoSetpointRequestedActivationFeedbackDk2', 'value'], '--'),
            dk2AAP: _.get(item, ['picassoAbsoluteActivationSignalDk2', 'value'], '--'),
            dk2CAP: _.get(item, ['picassoCurrentActivationSignalDk2', 'value'], '--'),
        }));

        const headers = [
            'Timestamp (CET)',
            'DK1 Heartbeat',
            'DK1 Setpoint Requested',
            'DK1 Setpoint Requested Feedback',
            'DK1 Absolute Activation Power',
            'DK1 Current Activation Power',
            'DK2 Heartbeat',
            'DK2 Setpoint Requested',
            'DK2 Setpoint Requested Feedback',
            'DK2 Absolute Activation Power',
            'DK2 Current Activation Power',
        ];

        processedData?.length > 0 && DownloadAsExcel(processedData, 'EIC Data Logs', headers);
    };

    return (
        <div>
            <div>
                <div className={AfrrClasses.Filter}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div className={AfrrClasses.FilterContainer}>
                            <div>
                                <div className={classes.FieldControl}>
                                    <label>Select EIC Code</label>
                                    <DropdownComponent
                                        name="eicCode"
                                        options={eicCodeOptions}
                                        defaultValue={{ value: selectedEicCode }}
                                        onChange={(e) => {
                                            if (e.value !== selectedEicCode) {
                                                setSelectedEicCode(e.value);
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={AfrrClasses.FilterContainer}>
                            <div className={AfrrClasses.LeftContainer}>
                                {date && (
                                    <div className={AfrrClasses.SelecterContainer}>
                                        <label className={AfrrClasses.DatePickerLabel}>Date</label>
                                        <DatePicker date={date} setDate={setDate} showArrow={true} />
                                    </div>
                                )}
                                {startTime && endTime && (
                                    <>
                                        <div className={AfrrClasses.SelecterContainer}>
                                            <label className={AfrrClasses.DatePickerLabel}>Start Time</label>
                                            <CustomTimePicker
                                                startTime={startTime}
                                                setStartTime={(e) => {
                                                    setStartTime(e);
                                                }}
                                                type="start"
                                                maxDetail={'second'}
                                            />
                                        </div>
                                        <div className={AfrrClasses.SelecterContainer}>
                                            <label className={AfrrClasses.DatePickerLabel}>End Time</label>
                                            <CustomTimePicker
                                                endTime={endTime}
                                                setEndTime={(e) => {
                                                    setEndTime(e);
                                                }}
                                                type="end"
                                                maxDetail={'second'}
                                            />
                                        </div>
                                    </>
                                )}
                                <div style={{ display: 'flex', gap: '0.5vw', alignItems: 'center' }}>
                                    <div onClick={() => getEicLogs(true)}>
                                        <button type="submit" className="btn-primary">
                                            Submit
                                        </button>
                                    </div>
                                    <div style={{ marginTop: '2vh' }} onClick={() => getEicLogs(false)}>
                                        <CustomTooltip content={'Download'}>
                                            <UilImport
                                                size="1.5vw"
                                                style={{
                                                    cursor: 'pointer',
                                                    color: 'var(--color-primary)',
                                                }}
                                            />
                                        </CustomTooltip>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={classes.AllDevices}>
                    <div className={classes.Header}>
                        <div>
                            <Typography content="EIC Values" />
                            <div className={classes.TableCount}>
                                Total Count :
                                <span>
                                    <Typography size="14" content={totalCount || 0} />
                                </span>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div>
                            <Table
                                head={[
                                    'Timestamp (CET)',
                                    'DK1 heartbeat',
                                    'DK1 setpoint requested',
                                    'DK1 Setpoint Requested Feedback',
                                    'DK1 Absolute Activation Power',
                                    'DK1 Current Activation Power',
                                    'DK2 Heartbeat',
                                    'DK2 Setpoint Requested',
                                    'DK2 Setpoint Requested Feedback',
                                    'DK2 Absolute Activation Power',
                                    'DK2 Current Activation Power',
                                ]}
                                keys={['timestamp', 'dk1H', 'dk1SR', 'dk1SRF', 'dk1AAP', 'dk1CAP', 'dk2H', 'dk2SR', 'dk2SRF', 'dk2AAP', 'dk2CAP']}
                                data={tableData}
                                page={page}
                                Pagination={true}
                                limit={LIMIT}
                                handlePageChange={handlePageChange}
                                totalCount={totalCount}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EicDataLogs;
